/**
 * HapticService - Manages haptic feedback (vibration)
 * 
 * Uses browser navigator.vibrate() API with fallback for unsupported browsers.
 * Respects user settings for haptic feedback.
 */

import { storageService } from './StorageService';

// Vibration patterns (in milliseconds)
const PATTERNS = {
  light: [10],           // Quick tap
  medium: [30],          // Normal feedback
  heavy: [50],           // Strong feedback
  success: [20, 50, 30], // Short-pause-long
  error: [50, 30, 50],   // Strong-pause-strong
  double: [20, 30, 20],  // Two quick taps
  notification: [30, 50, 30, 50, 30] // Pattern for achievements
};

class HapticService {
  constructor() {
    this.supported = this._checkSupport();
    this.enabled = true;
  }

  /**
   * Check if vibration API is supported
   */
  _checkSupport() {
    return 'vibrate' in navigator;
  }

  /**
   * Check if haptic feedback is enabled in settings
   */
  isEnabled() {
    return storageService.getSetting('hapticEnabled', true);
  }

  /**
   * Vibrate with a given pattern
   * @param {string|number[]} pattern - Pattern name or array of durations
   */
  vibrate(pattern = 'light') {
    // Check if enabled
    if (!this.isEnabled()) {
      return false;
    }

    // Check if supported
    if (!this.supported) {
      return false;
    }

    // Get pattern array
    let vibrationPattern;
    if (typeof pattern === 'string') {
      vibrationPattern = PATTERNS[pattern] || PATTERNS.light;
    } else if (Array.isArray(pattern)) {
      vibrationPattern = pattern;
    } else {
      vibrationPattern = [pattern];
    }

    try {
      return navigator.vibrate(vibrationPattern);
    } catch (e) {
      console.debug('HapticService: Vibration failed', e);
      return false;
    }
  }

  /**
   * Light haptic feedback (for button taps)
   */
  light() {
    return this.vibrate('light');
  }

  /**
   * Medium haptic feedback (for selections)
   */
  medium() {
    return this.vibrate('medium');
  }

  /**
   * Heavy haptic feedback (for important actions)
   */
  heavy() {
    return this.vibrate('heavy');
  }

  /**
   * Success haptic feedback (correct answer, code runs)
   */
  success() {
    return this.vibrate('success');
  }

  /**
   * Error haptic feedback (wrong answer, code error)
   */
  error() {
    return this.vibrate('error');
  }

  /**
   * Double tap feedback
   */
  doubleTap() {
    return this.vibrate('double');
  }

  /**
   * Notification feedback (achievement unlocked)
   */
  notification() {
    return this.vibrate('notification');
  }

  /**
   * Stop any ongoing vibration
   */
  stop() {
    if (this.supported) {
      try {
        navigator.vibrate(0);
      } catch (e) {
        // Ignore
      }
    }
  }

  /**
   * Enable or disable haptic feedback
   */
  setEnabled(enabled) {
    this.enabled = enabled;
    storageService.updateSetting('hapticEnabled', enabled);
  }
}

// Export singleton instance
export const hapticService = new HapticService();
export default hapticService;